import type Phaser from 'phaser';

export interface SlotInputCallbacks {
  onPointerOver(slotIndex: number): void;
  onPointerOut(slotIndex: number): void;
  onPointerDown(slotIndex: number, pointer: Phaser.Input.Pointer): void;
  onWheel(slotIndex: number, deltaY: number): void;
}

/**
 * Wires pointer events on each slot hotspot.
 *
 * - over/out drive the empty-slot ghost preview.
 * - down is the unified entry point for placement (empty slot) and rotation/removal
 *   (slot with mirror); the scene tracks per-slot state and disambiguates drag vs tap
 *   via scene-level pointermove/pointerup, which the scene listens for itself.
 * - wheel uses the scene's input plugin (not per-object) so we get the standard
 *   `currentlyOver` array; scrolling rotates the mirror in the slot under the pointer.
 */
export function attachSlotInput(
  scene: Phaser.Scene,
  hotspots: Phaser.GameObjects.Rectangle[],
  cb: SlotInputCallbacks,
): void {
  hotspots.forEach((hotspot, slotIndex) => {
    hotspot.setInteractive({ useHandCursor: true });
    hotspot.on('pointerover', () => cb.onPointerOver(slotIndex));
    hotspot.on('pointerout', () => cb.onPointerOut(slotIndex));
    hotspot.on('pointerdown', (p: Phaser.Input.Pointer) => cb.onPointerDown(slotIndex, p));
  });

  scene.input.on(
    'wheel',
    (
      _pointer: Phaser.Input.Pointer,
      currentlyOver: Phaser.GameObjects.GameObject[],
      _dx: number,
      deltaY: number,
    ) => {
      for (const go of currentlyOver) {
        const idx = hotspots.indexOf(go as Phaser.GameObjects.Rectangle);
        if (idx >= 0) {
          cb.onWheel(idx, deltaY);
          return;
        }
      }
    },
  );

  scene.input.mouse?.disableContextMenu();
}
